import { gsap } from 'gsap';
import { computed, reactive, ref, watch } from 'vue';

import { lerp } from '@resn/gozer-math';
import { usePane, useRafBool, useThreeObject } from '@resn/gozer-vue';

import { useFlare } from '~/components/gl/shaders/ShaderChunks/useFlare';

export const createLensflare = ({
    pos = { x: 0, y: 0, z: 0 },
    dir = 'x',
    distance = 0.65,
    color: _color = '#ffffff',
    bgColor: _bgColor = '#ffffff',
    baseScale = 0.2,
} = {}) => {
    const active = ref(false);

    const progress = ref(0);
    const totalDistance = ref(distance);
    const halfDistance = computed(() => totalDistance.value * 0.5);

    const mode = ref('');

    let playInterval;
    let playDelay;
    let tl;

    const { object } = useThreeObject(null, { nesting: false, props: { v: active } });

    const flare = useFlare({ name: 'StarGlow', scale: baseScale * 3 });
    flare.mesh.renderOrder = 4;
    object.add(flare.mesh);

    object.position.set(pos.x, pos.y, pos.z);

    const isComplete = ref(false);

    const startInterval = () => {
        stopInterval();

        playDelay = gsap.delayedCall(1, () => {
            playAnimation();

            playInterval = setInterval(() => {
                playAnimation();
            }, 4000);
        });
    };

    const stopInterval = () => {
        playDelay?.kill();
        clearInterval(playInterval);
    };

    const setMode = (newMode) => {
        if (mode.value === newMode) return;

        tl?.kill();
        stopInterval();

        gsap.to(flare.refs.uFlareAlpha, {
            value: 0,
            duration: flare.refs.uFlareAlpha.value > 0 ? 0.1 : 0,
            onComplete: () => {
                mode.value = newMode;
                if (newMode === 'ripping') {
                    active.value = true;
                    flare.refs.uFlareRayIntensity.value = 1.2;
                    gsap.to(flare.refs.uFlareAlpha, {
                        value: 1,
                        duration: 0.5,
                        ease: 'power2.out',
                    });
                } else {
                    active.value = false;
                    flare.refs.uFlareRayIntensity.value = 1;
                    startInterval();
                }
            },
        });
    };

    const startProgress = () => {
        setMode('ripping');
    };

    const setProgress = (val) => {
        if (mode.value !== 'ripping') return;
        progress.value = val;
    };

    const complete = () => {
        isComplete.value = true;
        active.value = false;

        tl?.kill();
        stopInterval();
        gsap.to(flare.refs.uFlareAlpha, {
            value: 0,
            duration: 0.1,
            ease: 'power2.in',
            onComplete: () => {
                active.value = false;
                mode.value = '';
            },
        });
    };

    const playAnimation = () => {
        tl?.kill();
        tl = gsap.timeline({
            onStart: () => (active.value = true),
            onComplete: () => (active.value = false),
        });

        progress.value = 0;
        flare.refs.uFlareRotation.value = 0;

        const duration = 1.6;
        const halfDuration = duration / 2;

        tl.fromTo(
            flare.refs.uFlareAlpha,
            { value: 0 },
            { value: 1, duration: halfDuration, ease: 'power2.out' },
            0
        );
        tl.fromTo(
            flare.refs.uFlareRotation,
            { value: 0 },
            { value: -6.28, duration, ease: 'linear' },
            0
        );
        tl.fromTo(progress, { value: 0 }, { value: 1, duration, ease: 'none' }, 0);

        tl.to(
            flare.refs.uFlareAlpha,
            { value: 0, duration: 0.2, ease: 'power2.in' },
            duration - 0.2
        );
    };

    const _pos = reactive(pos);
    watch(_pos, (val) => {
        object.position.set(val.x, val.y, val.z);
    });

    // const pane = usePane(
    //     [
    //         { name: 'progress', value: progress, options: { min: 0, max: 1, step: 0.01 } },
    //         {
    //             name: 'totalDistance',
    //             value: totalDistance,
    //             options: { min: 0, max: 2, step: 0.01 },
    //         },
    //     ],
    //     {
    //         title: 'StarGlow',
    //     }
    // );

    // pane?.addButton({ title: 'Show', label: 'Front Image' }).on('click', playAnimation);

    useRafBool(active, () => {
        if (mode.value === 'ripping') {
            flare.refs.uFlareRotation.value = progress.value * totalDistance.value * -6;
        }

        if (dir === 'x') {
            object.position.x = lerp(progress.value, -halfDistance.value, halfDistance.value);
        } else {
            object.position.y = lerp(progress.value, halfDistance.value, -halfDistance.value);
        }
    });

    return {
        mesh: flare.mesh,
        object,
        startInterval,
        stopInterval,
        setProgress,
        startProgress,
        setMode,
        complete,
        mode,
        isComplete,
    };
};
